import { FormValidation, UiSchema } from '@rjsf/utils'

import { calculateDefaultStairLocation } from '../components/markup/utils/components/material-forms/form-hooks/calculateDefaultStairLocation'
import { FormOnUpdateContext, FormProperties } from '../imup/types'
import { otherValue } from '../shared/constants/general-enums/value-other'
import { convertFullNumberFractionToDecimal } from '../utils/formSchema/numberConversion'
import { applyOtherDimensionValues, clearOtherDimensionFields } from '../utils/formSchema/otherDimensions'

const PORCH_TYPE = 'EXTERIOR DECK / PORCH'
const DIMENSIONAL_LUMBER = 'DIMENSIONAL LUMBER'
const EWP = 'EWP'
const DWELLING_TYPE = 'DWELLING'

const TEMP_CONDITIONS = ['TEMPORARY', 'FINISHED AND TEMPORARY']

export const uiSchema: UiSchema = {
    type: {
        'ui:title': 'Type',
        'ui:placeholder': 'Select Type',
    },
    stair_width: {
        'ui:title': 'Stair Width',
        'ui:placeholder': 'Select Stair Width',
    },
    tread_condition: {
        'ui:title': 'Tread Condition',
        'ui:placeholder': 'Select Tread Condition',
    },
    riser_condition: {
        'ui:title': 'Riser Condition',
        'ui:placeholder': 'Select Riser Condition',
    },
    location: {
        'ui:title': 'Location',
        'ui:placeholder': 'Select Location',
    },
    number_of_staircases: {
        'ui:title': 'Number of staircases',
        'ui:placeholder': 'Number of staircases',
    },
    stair_width_inches: {
        'ui:title': 'Stair Width Inches',
        'ui:placeholder': 'Stair Width Inches',
    },
    number_of_treads: {
        'ui:title': 'Number of treads',
        'ui:placeholder': 'Number of treads',
    },
    number_garage_stairs: {
        'ui:title': 'Number garage stairs',
        'ui:placeholder': 'Number garage stairs',
    },
    ext_deck_stairs_width: {
        'ui:title': 'Ext Deck Stairs Width (Inches)',
        'ui:placeholder': 'Ext Deck Stairs Width',
    },
    ext_deck_stringer_length: {
        'ui:title': 'Ext Deck Stringer Length (Feet)',
        'ui:placeholder': 'Ext Deck Stringer Length',
    },
    ext_deck_stringer: {
        'ui:placeholder': 'Select Ext Deck Stringer',
    },
    ext_deck_riser: {
        'ui:placeholder': 'Select Ext Deck Riser',
    },
    ext_deck_tread_count: {
        'ui:title': 'Ext Deck Tread Count',
        'ui:placeholder': 'Ext Deck Tread Count',
    },
    stringer_material: {
        'ui:placeholder': 'Select Stringer Material',
    },
    dimensional_stringer_dimensions: {
        'ui:field': 'LumberDimensionsField',
    },
    ewp_stringer_dimensions: {
        'ui:field': 'LumberDimensionsField',
    },
    stringer_spacing: {
        'ui:title': 'Stringer Spacing',
        'ui:placeholder': 'Enter Stringer Spacing',
    },
    stringer_thickness_other: {
        'ui:title': 'Other Thickness',
        'ui:placeholder': 'Enter Other Thickness',
    },
    stringer_width_other: {
        'ui:title': 'Other Width',
        'ui:placeholder': 'Enter Other Width',
    },
    stairs_treads: {
        'ui:placeholder': 'Select Stair Treads',
    },
    stairs_risers: {
        'ui:placeholder': 'Select Stair Risers',
    },
    tread_material: {
        'ui:placeholder': 'Select Tread Material',
    },
    riser_material: {
        'ui:placeholder': 'Select Riser Material',
    },
    ewp_type: {
        'ui:placeholder': 'Select EWP Type',
    },
    version: {
        'ui:widget': 'hidden',
    },
    'ui:order': [
        'type',
        'location',
        'stairs_treads',
        'stairs_risers',
        'number_of_treads',
        'stair_width',
        'stair_width_inches',
        'tread_condition',
        'tread_material',
        'riser_condition',
        'riser_material',
        'number_of_staircases',
        'stringer_material',
        'dimensional_stringer_dimensions',
        'ewp_type',
        'ewp_stringer_dimensions',
        'stringer_thickness_other',
        'stringer_width_other',
        'stringer_spacing',
        'number_garage_stairs',
        'ext_deck_stairs_width',
        'ext_deck_stringer_length',
        'ext_deck_riser',
        'ext_deck_stringer',
        'ext_deck_tread_count',
    ],
}

const onValidate = (formData: any, errors: FormValidation) => {
    if (formData.stringer_material === EWP) {
        if (formData.ewp_stringer_dimensions?.thickness === otherValue && formData.stringer_thickness_other) {
            const thicknessOther = Number(convertFullNumberFractionToDecimal(formData.stringer_thickness_other))

            if (thicknessOther && thicknessOther >= 100) {
                errors.stringer_thickness_other?.addError('Must be less than 100')
            }
        }
        if (formData.ewp_stringer_dimensions?.width === otherValue && formData.stringer_width_other) {
            const widthOther = Number(convertFullNumberFractionToDecimal(formData.stringer_width_other))

            if (widthOther && widthOther >= 100) {
                errors.stringer_width_other?.addError('Must be less than 100')
            }
        }
    }
}

const onSubmit = (settings, formData) => {
    let newSettings = { ...settings, ...formData }

    if (!newSettings.version) {
        if (!newSettings.stairs_treads) {
            newSettings.stairs_risers = null
            newSettings.riser_material = null
            newSettings.number_of_treads = null
            newSettings.stair_width_inches = null
            newSettings.tread_material = null
        }

        if (newSettings.stairs_treads === 'Finished Only') {
            newSettings.tread_material = null
        }

        if (!newSettings.stairs_risers || newSettings.stairs_risers === 'Finished Only') {
            newSettings.riser_material = null
        }
    } else if (parseInt(newSettings.version) === 2) {
        if (!TEMP_CONDITIONS.includes(newSettings.tread_condition)) {
            newSettings.tread_material = null
        }

        if (!TEMP_CONDITIONS.includes(newSettings.riser_condition)) {
            newSettings.riser_material = null
        }
    }

    if (parseInt(newSettings.version) === 2) {
        if (newSettings.stringer_material !== DIMENSIONAL_LUMBER) {
            newSettings.dimensional_stringer_dimensions = null
        }

        if (newSettings.stringer_material !== EWP) {
            newSettings.ewp_stringer_dimensions = null
        }
    }

    newSettings = applyOtherDimensionValues(newSettings, {
        dimensionsKey: 'dimensional_stringer_dimensions',
        thicknessOtherKey: 'stringer_thickness_other',
        widthOtherKey: 'stringer_width_other',
    })
    newSettings = applyOtherDimensionValues(newSettings, {
        dimensionsKey: 'ewp_stringer_dimensions',
        thicknessOtherKey: 'stringer_thickness_other',
        widthOtherKey: 'stringer_width_other',
        shouldConvertFractions: true,
    })

    newSettings = clearOtherDimensionFields(newSettings, ['stringer_thickness_other', 'stringer_width_other'])

    return newSettings
}

const onUpdate = (
    previousFormData,
    formData,
    formSchema,
    { activeDocumentMapping, activeDrawableGroup, regions }: FormOnUpdateContext
) => {
    if (parseInt(formData.version) !== 2) return formData

    if (formData.type === PORCH_TYPE && formSchema.definitions?.stairs_exterior_location) {
        formData.location = formSchema.definitions.stairs_exterior_location.default
    }

    if (formData.type === DWELLING_TYPE) {
        const defaultDwellingLocation = calculateDefaultStairLocation(
            activeDocumentMapping,
            formSchema,
            activeDrawableGroup,
            regions
        )

        if (defaultDwellingLocation && previousFormData.type !== DWELLING_TYPE) {
            formData.location = defaultDwellingLocation
        }
    }

    return formData
}

const form: FormProperties = { uiSchema, onValidate, onSubmit, onUpdate }

export default form
